import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { WebService } from '../../services/web/web.service';
import { SecurityService } from '../../services/security.service';

export interface ReactivableInvoice {
    customerHasPlanId: number;
    invoiceId: number;
    invoiceNumber: string;
    invoiceDate: string;
    nextBillDate: string;
    amount: number;
}

export const ReactivationConflictCode = {
    ALREADY_REACTIVATED: 'ALREADY_REACTIVATED',
    NOT_ELIGIBLE: 'NOT_ELIGIBLE',
    STATUS_CHANGED: 'STATUS_CHANGED'
} as const;

export type ReactivationConflictCodeValue =
    typeof ReactivationConflictCode[keyof typeof ReactivationConflictCode];

@Injectable({
    providedIn: 'root'
})
export class InvoiceReactivationService {

    constructor(
        private webService: WebService,
        private securityService: SecurityService
    ) {}

    getReactivable(customerId: number): Observable<ReactivableInvoice[]> {
        const partnerId = this.getValidPartnerId();
        if (!customerId || partnerId === null) {
            return of([]);
        }
        const url = `${this.webService.HOST}/invoice/partner/${partnerId}/customer/${customerId}/reactivable`;
        return this.webService.get(url).pipe(
            map(res => {
                // Contrato confirmado: { message, result: [...], pager }.
                // `result.entities` se mantiene solo como fallback tolerante.
                const result = res?.result;
                if (Array.isArray(result)) {
                    return result;
                }
                return result?.entities ?? [];
            })
        );
    }

    reactivate(invoiceId: number): Observable<any> {
        const partnerId = this.getValidPartnerId();
        if (partnerId === null) {
            return throwError(() => new Error('Partner inválido para reactivar el cobro'));
        }
        if (!Number.isInteger(invoiceId) || invoiceId <= 0) {
            return throwError(() => new Error('invoiceId inválido para reactivar el cobro'));
        }
        const url = `${this.webService.HOST}/invoice/partner/${partnerId}/${invoiceId}/reactivate-billing`;
        // El proxy responde 500 con body nulo: enviar siempre {}.
        return this.webService.putPreservingHttpError({}, url);
    }

    private getValidPartnerId(): number | null {
        const partnerId = Number(this.securityService.getTokenPartner());
        return Number.isFinite(partnerId) && partnerId > 0 ? partnerId : null;
    }

    /**
     * Extrae el código de conflicto (409) de forma tolerante a la ubicación en el body.
     */
    extractConflictCode(err: any): ReactivationConflictCodeValue | null {
        const candidates = [err?.error?.code, err?.error?.result?.code, err?.error?.message];
        for (const candidate of candidates) {
            if (typeof candidate !== 'string') {
                continue;
            }
            const found = Object.values(ReactivationConflictCode).find(code => candidate.includes(code));
            if (found) {
                return found;
            }
        }
        return null;
    }
}
